"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { MoreVertical, ArrowLeft, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import type { TrainingBrief } from "@/lib/types/training";
import { Button } from "@workspace/ui/components";
import { MobileHeader } from "../../components/mobile-header";
import { DesktopHeader } from "../../components/desktop-header";

type ProspectBriefProps = {
  brief: TrainingBrief;
};

export function ProspectBrief({ brief }: ProspectBriefProps) {
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "1",
    "2",
    "3",
  ]);

  // Parse content into sections
  const sections = [
    {
      number: "1",
      title: "Can you save your SaaS?",
      content: brief.meetingContext,
    },
    {
      number: "2",
      title: "Why this matters?",
      content: brief.mediaCoverage,
    },
    {
      number: "3",
      title: "What You'll Discover",
      content: brief.roleBackground,
    },
  ];

  const toggleSection = (number: string) => {
    setExpandedSections((prev) =>
      prev.includes(number)
        ? prev.filter((n) => n !== number)
        : [...prev, number],
    );
  };

  return (
    <div className="w-full min-h-screen pb-[150px] md:pb-0">
      {/* Desktop Header */}
      <DesktopHeader />
      <MobileHeader />

      {/* Main Content */}
      <div className="flex flex-col gap-[48px] md:gap-[48px] items-center pb-[64px] pt-[120px] md:pt-[40px] px-[20px] md:px-[40px] w-full">
        {/* Desktop Prospect Section */}
        <div className="hidden md:flex bg-white border border-[#e3d8c4] border-solid flex-col gap-[24px] items-start p-[20px] rounded-[32px] shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)] w-full max-w-[800px]">
          <div className="bg-[#fff3dc] border border-[#e3d8c4] border-solid flex flex-col items-start px-[32px] py-[40px] rounded-[20px] w-full">
            <div className="flex gap-[24px] items-center w-full">
              <div className="bg-white border-[3px] border-[#292524] border-solid rounded-full shadow-[4px_4px_0px_0px_#292524] shrink-0 size-[96px] overflow-hidden">
                <Image
                  src="/marcus.png"
                  alt="marcus"
                  width={96}
                  height={96}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-1 flex-col gap-[8px] items-start">
                <p className="font-extrabold leading-[32px] text-[#292524] text-[28px] w-full">
                  {brief.prospect.name}
                </p>
                <p className="font-medium leading-[24px] text-[#797268] text-[18px] w-full">
                  {brief.prospect.role}
                </p>
              </div>
              <Link
                href={`/training/session/${brief.id}`}
                className="border-2 border-solid border-white flex gap-[10px] items-center justify-center px-[20px] py-[13px] rounded-[12px] shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)] shrink-0"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(255, 255, 255, 0.56) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, rgb(255, 214, 2) 0%, rgb(255, 214, 2) 100%)",
                }}
              >
                <p className="capitalize font-semibold leading-[22px] text-[#292524] text-[18px]">
                  Start Roleplay
                </p>
              </Link>
            </div>
          </div>

          {/* Desktop Meeting Context */}
          <div className="flex flex-col gap-[20px] items-start w-full pt-[4px]">
            <p className="font-semibold leading-[28px] text-[#292524] text-[20px]">
              Meeting Context
            </p>
            <div className="flex flex-col gap-[16px] items-start w-full">
              {sections.map((section, idx) => (
                <div
                  key={idx}
                  className="flex flex-col gap-[8px] items-start w-full"
                >
                  <div className="flex gap-[10px] items-center w-full">
                    <div className="bg-[#f3e4c9] flex items-center justify-center w-[24px] h-[24px] rounded-full shrink-0">
                      <span className="font-black text-[#292524] text-[14px] tracking-[-0.28px]">
                        {section.number}
                      </span>
                    </div>
                    <p className="flex-1 font-semibold leading-[24px] text-[#292524] text-[18px]">
                      {section.title}
                    </p>
                  </div>
                  <div className="flex items-center justify-center px-[34px] w-full">
                    <p className="flex-1 font-medium leading-[24px] text-[#797268] text-[16px]">
                      {section.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-[#d8f1f7] flex items-center justify-center px-[20px] py-[16px] rounded-[16px] w-full">
              <p className="flex-1 font-medium leading-[22px] text-[#4f94ae] text-[16px]">
                Before starting this activity, please find a quiet place with
                minimal distractions. You'll need a microphone and a video
                camera to participate in this activity.
              </p>
            </div>
          </div>
        </div>

        {/* Mobile Content */}
        <div className="md:hidden w-full flex flex-col gap-[24px]">
          <h1 className="font-semibold text-[24px] leading-[32px] text-[#292524]">
            Meeting Context
          </h1>
          <div className="bg-white border border-[#e3d8c4] flex flex-col gap-[20px] p-[16px] rounded-[24px] shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)] w-full">
            {sections.map((section, idx) => (
              <div key={idx} className="flex flex-col w-full">
                <button
                  onClick={() => toggleSection(section.number)}
                  className="flex gap-[10px] items-center w-full pb-3"
                >
                  <div className="bg-[#f3e4c9] flex items-center justify-center w-[20px] h-[20px] rounded-full shrink-0">
                    <span className="font-black text-[#292524] text-[11px] tracking-[-0.22px]">
                      {section.number}
                    </span>
                  </div>
                  <p className="flex-1 font-medium text-left leading-[24px] text-[#292524] text-[16px]">
                    {section.title}
                  </p>
                  <motion.div
                    animate={{
                      rotate: expandedSections.includes(section.number)
                        ? 180
                        : 0,
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <ChevronDown className="size-[20px] text-[#292524]" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false}>
                  {expandedSections.includes(section.number) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pl-[30px] pr-[12px]">
                        <p className="font-medium leading-[20px] text-[#797268] text-[14px]">
                          {section.content}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
            <div className="bg-[#d8f1f7] flex items-center justify-center px-[20px] py-[16px] rounded-[16px] w-full">
              <p className="flex-1 font-medium leading-[20px] text-[#4f94ae] text-[14px]">
                Before starting this activity, please find a quiet place with
                minimal distractions. You'll need a microphone and a video
                camera to participate in this activity.
              </p>
            </div>
          </div>
        </div>

        {/* Desktop Sign Up Button */}
        <button className="hidden md:flex bg-white border-2 border-[#e3d8c4] border-solid gap-[10px] items-center justify-center px-[20px] py-[13px] rounded-[12px] shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)] shrink-0">
          <p className="capitalize font-semibold leading-[22px] text-[#292524] text-[18px]">
            Sign Up Now
          </p>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 15L15 5M15 5H8M15 5V12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Mobile Bottom Prospect Section */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-20">
        <div className="bg-white border-t border-l border-r border-[#e3d8c4] flex flex-col items-center justify-center p-[20px] rounded-tl-[24px] rounded-tr-[24px]">
          <div className="flex gap-[16px] h-[56px] items-center w-full">
            <div className="bg-white border-[1.75px] border-[#292524] border-solid rounded-full shadow-[2.333px_2.333px_0px_0px_#292524] shrink-0 size-[56px] flex items-center justify-center overflow-hidden">
              <Image
                src="/marcus.png"
                alt="marcus"
                width={96}
                height={96}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-1 flex-col gap-[4px] items-start">
              <p className="font-extrabold leading-[28px] text-[#292524] text-[20px] w-full">
                {brief.prospect.name}
              </p>
              <p className="font-medium leading-[20px] text-[#797268] text-[14px] w-full">
                {brief.prospect.role}
              </p>
            </div>
            <Button variant="d-primary" asChild className="h-10 text-base">
              <Link href={`/training/session/${brief.id}`}>Start Roleplay</Link>
            </Button>
          </div>
        </div>
        <div className="bg-white h-[34px] w-full" />
      </div>
    </div>
  );
}
